// growthos/warchest-frontend/react-app/src/pages/scenario-builder/v3/pages/scenario-results/components/HighLevelOverview.tsx
import { Box, useMediaQuery } from "@mui/material";
import { useMemo } from "react";

import { components } from "@/@types/api";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { EN_DASH } from "@/pages/scenario-builder/constants";
import { isDefined } from "@/Utils/array-utils";

import { getFunnelStageBreakdown } from "../../../utils/getFunnelStageBreakdown";
import { FunnelStageCard } from "./FunnelStageCard";
import { SummaryMetric } from "./SummaryMetric";

type Revenue = components["schemas"]["ScenarioResultRevenue"];
type Spend = components["schemas"]["ScenarioResultSpend"];

// Consistent height for small screen mode cards
const SMALL_SCREEN_CARD_HEIGHT = 140;

interface HighLevelOverviewProps {
  simulatedSpend: number;
  simulatedRevenue: number;
  minimumRevenue: number;
  maximumRevenue: number;
  minimumROI: number | undefined;
  maximumROI: number | undefined;
  averageROI: number | undefined;
  selectedGeography: string | null;
  summaryFormatter: (value: number) => string;
  formatPercentageFromFraction: (value: number) => string;
  revenues: Revenue[];
  spends: Spend[];
}

export const HighLevelOverview = ({
  simulatedSpend,
  simulatedRevenue,
  minimumRevenue,
  maximumRevenue,
  minimumROI,
  maximumROI,
  averageROI,
  selectedGeography,
  summaryFormatter,
  formatPercentageFromFraction,
  revenues,
  spends,
}: HighLevelOverviewProps) => {
  const isLargeScreen = useMediaQuery("(min-width:1400px)");

  // Metrics logic
  const formattedAverageROI = isDefined(averageROI)
    ? formatPercentageFromFraction(averageROI)
    : "-";
  const formattedMinimumROI = isDefined(minimumROI)
    ? formatPercentageFromFraction(minimumROI)
    : "-";
  const formattedMaximumROI = isDefined(maximumROI)
    ? formatPercentageFromFraction(maximumROI)
    : "-";

  // Funnel stage breakdown logic
  const stageBreakdown = useMemo(() => {
    return getFunnelStageBreakdown(revenues, spends);
  }, [revenues, spends]);

  if (isLargeScreen) {
    return (
      <Box pt={3} pb={3} mb={3}>
        {/* Metrics Row */}
        <Stack direction="row" alignItems="stretch" gap={2} mb={3}>
          <SummaryMetric
            label="Simulated Spend"
            metric={summaryFormatter(simulatedSpend || 0)}
            sx={{ flex: 1 }}
          />
          <SummaryMetric
            label="Simulated Revenue"
            metric={summaryFormatter(simulatedRevenue || 0)}
            byline={
              selectedGeography === null
                ? `(${summaryFormatter(minimumRevenue)} ${EN_DASH} ${summaryFormatter(maximumRevenue)})`
                : undefined
            }
            bylineTooltip={
              selectedGeography === null
                ? "Our model expects the revenue in this scenario to be within the upper and lower bounds of this range. The number above represents its midpoint."
                : undefined
            }
            sx={{ flex: 1 }}
          />
          <SummaryMetric
            label="Simulated ROI"
            metric={formattedAverageROI}
            byline={
              selectedGeography === null
                ? `(${formattedMinimumROI} ${EN_DASH} ${formattedMaximumROI})`
                : undefined
            }
            bylineTooltip={
              selectedGeography === null
                ? "Our model expects the ROI in this scenario to be within the upper and lower bounds of this range. The number above represents its midpoint."
                : undefined
            }
            sx={{ flex: 1 }}
          />
        </Stack>

        {/* Funnel Stages Row */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 2,
            alignItems: "stretch",
          }}
        >
          {stageBreakdown.length === 0 ? (
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body1" textAlign="center">
                All spend and revenue allocated to Brand Marketing
              </Typography>
            </Box>
          ) : stageBreakdown.length === 1 ? (
            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body1" textAlign="center">
                All spend and revenue allocated to {stageBreakdown[0]?.stage || "Brand Marketing"}
              </Typography>
            </Box>
          ) : (
            stageBreakdown.map((stage) => (
              <FunnelStageCard
                key={stage.stage}
                stage={stage.stage}
                spend={stage.spend}
                revenue={stage.revenue}
                roi={stage.roi}
                spendPercentage={stage.spendPercentage}
                revenuePercentage={stage.revenuePercentage}
                summaryFormatter={summaryFormatter}
              />
            ))
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box pt={3} pb={1} mb={3}>
      {/* Combined Grid Layout for Small Screens */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridAutoFlow: "column",
          gap: 2,
          alignItems: "stretch",
        }}
      >
        {/* Column 1: [1] [2] [3] */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SummaryMetric
            label="Simulated Spend"
            metric={summaryFormatter(simulatedSpend || 0)}
            sx={{ height: SMALL_SCREEN_CARD_HEIGHT }}
          />
          <SummaryMetric
            label="Simulated Revenue"
            metric={summaryFormatter(simulatedRevenue || 0)}
            byline={
              selectedGeography === null
                ? `(${summaryFormatter(minimumRevenue)} ${EN_DASH} ${summaryFormatter(maximumRevenue)})`
                : undefined
            }
            bylineTooltip={
              selectedGeography === null
                ? "Our model expects the revenue in this scenario to be within the upper and lower bounds of this range. The number above represents its midpoint."
                : undefined
            }
            sx={{ height: SMALL_SCREEN_CARD_HEIGHT }}
          />
          <SummaryMetric
            label="Simulated ROI"
            metric={formattedAverageROI}
            byline={
              selectedGeography === null
                ? `(${formattedMinimumROI} ${EN_DASH} ${formattedMaximumROI})`
                : undefined
            }
            bylineTooltip={
              selectedGeography === null
                ? "Our model expects the ROI in this scenario to be within the upper and lower bounds of this range. The number above represents its midpoint."
                : undefined
            }
            sx={{ height: SMALL_SCREEN_CARD_HEIGHT }}
          />
        </Box>

        {/* Column 2: [4] [5] [6] - Funnel Stages */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {stageBreakdown.length === 0 ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: SMALL_SCREEN_CARD_HEIGHT,
                }}
              >
                <Typography variant="body1" textAlign="center">
                  All spend and revenue allocated to Brand Marketing
                </Typography>
              </Box>
              <Box sx={{ height: SMALL_SCREEN_CARD_HEIGHT }} />
              <Box sx={{ height: SMALL_SCREEN_CARD_HEIGHT }} />
            </>
          ) : stageBreakdown.length === 1 ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: SMALL_SCREEN_CARD_HEIGHT,
                }}
              >
                <Typography variant="body1" textAlign="center">
                  All spend and revenue allocated to {stageBreakdown[0]?.stage || "Brand Marketing"}
                </Typography>
              </Box>
              <Box sx={{ height: SMALL_SCREEN_CARD_HEIGHT }} />
              <Box sx={{ height: SMALL_SCREEN_CARD_HEIGHT }} />
            </>
          ) : (
            <>
              {stageBreakdown.map((stage) => (
                <FunnelStageCard
                  key={stage.stage}
                  stage={stage.stage}
                  spend={stage.spend}
                  revenue={stage.revenue}
                  roi={stage.roi}
                  spendPercentage={stage.spendPercentage}
                  revenuePercentage={stage.revenuePercentage}
                  summaryFormatter={summaryFormatter}
                  sx={{ height: SMALL_SCREEN_CARD_HEIGHT }}
                />
              ))}
              {/* Add spacers to maintain alignment */}
              {Array.from({ length: 3 - stageBreakdown.length }, (_, index) => (
                <Box key={`spacer-${index}`} sx={{ height: SMALL_SCREEN_CARD_HEIGHT }} />
              ))}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};
